import { Observable, fromEventPattern } from 'rxjs';
import { InvokeCallback } from 'xstate';
import { Hub } from '@aws-amplify/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { UserSettings } from '@alucio/aws-beacon-amplify/src/models';
import { observeSlice } from 'src/state/redux';
import { SliceState } from 'src/state/redux/slice/common';
import {
  EVT_ANALYTICS_ACTION_TRIGGERED,
  EVT_UPDATE_CONTEXT_USER_SETTINGS,
} from './analytics/analytics.types';
import * as logger from 'src/utils/logger';
import ActiveUser from '../global/ActiveUser';
import { ROUTES, DNARoute } from 'src/router/routes';
import reduce from 'lodash/reduce';

export type EVT_USER_SIGNED_IN = {
  type: 'USER_SIGNED_IN',
}

export type EVT_USER_SIGNED_OUT = {
  type: 'USER_SIGNED_OUT',
}

export type AnalyticEvent = [string, { [fieldName: string]: any } | undefined];

export type EVT_UPDATE_CURRENT_ROUTE = {
  type: 'UPDATE_CURRENT_ROUTE',
  payload?: DNARoute,
};

function getCurrentDNARoute(pathname: string): DNARoute | undefined {
  const homeRegex = [
    /^\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/\d+\/$/,
    /^\/$/,
  ];
  if (homeRegex.some(regex => regex.test(pathname))) {
    return ROUTES.HOME;
  }
  const substrings = new Set(pathname.split('/').filter(segment => segment !== ''));
  const countMatches = (str: string) => str.split('/').reduce((a, substr) => a + Number(substrings.has(substr)), 0);
  const routeSearch: { bestMatch?: DNARoute, count?: number } = {};
  const { bestMatch } = reduce(ROUTES, ({ bestMatch, count = 0 }, route) => {
    const thisCount = countMatches(route.PATH);
    return thisCount > count
      ? { count: thisCount, bestMatch: route }
      : { count, bestMatch };
  }, routeSearch);
  return bestMatch;
}

export function createRouteChangeObservable(history) {
  return fromEventPattern(
    (handler) => {
      const unlisten = history.listen((location) => {
        handler({
          type: 'UPDATE_CURRENT_ROUTE',
          payload: getCurrentDNARoute(location.pathname),
        });
      });
      // Emit initial route
      handler({
        type: 'UPDATE_CURRENT_ROUTE',
        payload: getCurrentDNARoute(history.location.pathname),
      });
      return unlisten;
    },
    (unlisten) => {
      if (typeof unlisten === 'function') unlisten();
    },
  );
}

async function checkInitialAuthState(
  send: (event: 'USER_SIGNED_IN') => void,
): Promise<void> {
  try {
    const session = await fetchAuthSession();
    if (session.tokens?.idToken) {
      send('USER_SIGNED_IN')
    }
  } catch (error) {
    console.error('Error checking initial auth state')
  }
}

export const authActor: InvokeCallback<
  any,
  EVT_USER_SIGNED_IN | EVT_USER_SIGNED_OUT
> = (send) => {
  checkInitialAuthState(send)

  const unregister = Hub.listen('auth', (capsule) => {
    switch (capsule.payload.event) {
      case 'signedIn': {
        send('USER_SIGNED_IN')
        break;
      }
      case 'signedOut': {
        send('USER_SIGNED_OUT')
        break;
      }
    }
  })

  return unregister
}

export const activeUserActor: InvokeCallback<
  any,
  EVT_USER_SIGNED_IN | EVT_USER_SIGNED_OUT
> = (send) => {
  const unregister = ActiveUser.listen((user) => {
    if (user) {
      send('USER_SIGNED_IN')
    } else {
      send('USER_SIGNED_OUT')
    }
  })

  return unregister
}

export const userSettingsObservable = () => {
  return observeSlice<SliceState<UserSettings[]>>(
    state => state.userSettings.records,
    undefined,
  ).map<EVT_UPDATE_CONTEXT_USER_SETTINGS>(userSettings => {
    return {
      type: 'UPDATE_CONTEXT_USER_SETTINGS',
      payload: userSettings[0],
    }
  })
}

export const analyticsObservable = (): Observable<EVT_ANALYTICS_ACTION_TRIGGERED> => {
  return new Observable((observer) => {
    const originalTrack = window.analytics?.track;

    if (typeof originalTrack !== 'function') {
      logger.analytics.machine.warn('Analytics track function not found.');
      return () => {}; // Cleanup function does nothing
    }

    analyticsEventDispatcher.setListener((event) => {
      observer.next({
        type: 'ANALYTICS_ACTION_TRIGGERED',
        payload: event,
      });
    });

    return () => {
      analyticsEventDispatcher.clearListener();
    };
  });
};

class AnalyticsEventDispatcher {
  private listener: ((event: AnalyticEvent) => void) | null = null;

  public dispatchAnalyticEvent(event: AnalyticEvent): void {
    this.listener?.(event);
  }

  public setListener(newListener: (event: AnalyticEvent) => void): void {
    this.listener = newListener;
  }

  public clearListener(): void {
    this.listener = null;
  }
}

export const analyticsEventDispatcher = new AnalyticsEventDispatcher();

export default {
  authActor,
  userSettingsObservable,
  analyticsObservable,
  analyticsEventDispatcher,
}
